// @flow
import { useDispatch } from "react-redux";

import { useModularUIBasic } from "./useModularUIBasic";
import { HTTP_METHODS } from "../constants/Constants";

import { loadModularUI } from "../redux";

import { useNavigation } from "./useRouter";

import DetailModel from "../models/detail/DetailModel";
import ListModel from "../models/list/ListModel";
import CaseSearchModel from "../models/search/CaseSearchModel";
import ListDetailModel from "../models/list/ListDetailModel";
import Href from "../models/href/Href";
import FilterCollection from "../models/filters/FilterCollection";
import AssignmentFilterModel from "../models/filters/AssignmentFilterModel";
import FilterModel from "../models/filters/FilterModel";

import type { HookOptions } from "./useModularUIBasic";
import type { AttributeType, IFilter } from "../models/types";
import { useCallback, useMemo } from "react";

type ListNavigationHook = {
  update: (list: ListModel, resetPage?: boolean) => void,
  updateFilters: (
    list: ListModel,
    filters: FilterCollection | Array<IFilter>,
  ) => void,
  updateFilter: (
    list: ListModel,
    filter: IFilter,
    value: string,
    attribute?: AttributeType,
  ) => void,
  updateSort: (list: ListModel, sortOption: string) => void,
  updatePage: (list: ListModel, page: number) => void,
  updatePageSize: (list: ListModel, pagesize: number) => void,
  resetFilters: (list: ListModel) => void,
  removeFilter: (list: ListModel, filter: IFilter) => void,
};

type ListNavigationOptions = {
  syncLocation?: boolean,
  historyAction?: "push" | "replace",
};

/**
 */
export const useList = (
  href: string | Href,
  options?: HookOptions,
): ListModel | null =>
  useModularUIBasic("list", href, {
    expectedModels: ["List", "CaseSearch"],
    targetModel: [ListModel, CaseSearchModel],
    ...options,
  });

/**
 */
export const useListOrDetail = (
  href: string | Href,
  options?: HookOptions,
): ListModel | DetailModel | null =>
  useModularUIBasic("list", href, {
    expectedModels: ["List", "CaseSearch", "Detail"],
    targetModel: [ListModel, CaseSearchModel, DetailModel],
    ...options,
  });

/**
 */
export const useListDetail = (
  href: string | Href,
  options?: HookOptions,
): ListDetailModel | null =>
  useModularUIBasic("listdetail", href, {
    expectedModels: ["ListDetail"],
    targetModel: ListDetailModel,
    forceTargetModel: true,
    ...options,
  });

/**
 */
export const useListNavigation = ({
  syncLocation = true,
  historyAction = "push",
}: ListNavigationOptions = {}): ListNavigationHook => {
  const { push, replace } = useNavigation();
  const dispatch = useDispatch();

  const navigate = historyAction === "push" ? push : replace;

  const update = useCallback(
    (list: ListModel, resetPage: boolean = false) => {
      const listHref = list.selfhref;
      if (resetPage) {
        listHref.page = null;
      }

      if (list.requestMethod === HTTP_METHODS.POST) {
        if (syncLocation) {
          navigate({
            pathname: listHref.path,
            search: listHref.getQuerystring(true),
            state: { formdata: list.formdata },
          });
        }

        dispatch(
          loadModularUI(list.connectKey, listHref, {
            method: HTTP_METHODS.POST,
            data: list.formdata,
            targetModel: [ListModel, CaseSearchModel],
          }),
        );
      } else {
        if (syncLocation) {
          navigate({
            pathname: listHref.path,
            search: listHref.getQuerystring(true),
          });
        }
        dispatch(
          loadModularUI(list.connectKey, listHref, {
            method: HTTP_METHODS.GET,
            targetModel: [ListModel, CaseSearchModel],
          }),
        );
      }
    },
    [dispatch, navigate, syncLocation],
  );

  const updateFilters = useCallback(
    (list: ListModel, filters: FilterCollection | Array<IFilter>) => {
      list.filterCollection = filters;
      update(list, true);
    },
    [update],
  );

  const updateFilter = useCallback(
    (
      list: ListModel,
      filter: IFilter,
      value: string,
      attribute?: AttributeType,
    ) => {
      if (filter instanceof AssignmentFilterModel && !attribute) {
        throw new TypeError(
          "AssignmentFilterModel needs the attribute argument to be able to update the filter",
        );
      }

      let filterToUpdate = null;
      let targetAttr = attribute;

      if (attribute) {
        filterToUpdate = list.filterCollection.getFilterByAttributeKey(
          attribute.key,
        );
      } else if (filter instanceof FilterModel) {
        filterToUpdate = list.filterCollection.getFilterByName(filter.name);
        targetAttr = filter.attribute;
      }

      if (filterToUpdate && targetAttr) {
        filterToUpdate.update(targetAttr, value);
      } else {
        const searchKey = attribute
          ? `key: ${attribute.key}`
          : `name: ${filter.name}`;
        console.error(
          `Can't find a filter to update by ${searchKey} on list: ${list.selfhref.toString()}`,
        );
      }

      update(list, true);
    },
    [update],
  );

  const resetFilters = useCallback(
    (list: ListModel) => {
      list.filterCollection.reset();
      update(list, true);
    },
    [update],
  );

  const removeFilter = useCallback(
    (list: ListModel, filter: IFilter) => {
      list.filterCollection.getFilterByName(filter.name)?.reset();
      update(list, true);
    },
    [update],
  );

  const updateSort = useCallback(
    (list: ListModel, sortOption: string) => {
      list.sorting.value = sortOption;
      update(list, true);
    },
    [update],
  );

  const updatePage = useCallback(
    (list: ListModel, page: number) => {
      list.paging.page = page;
      update(list);
    },
    [update],
  );

  const updatePageSize = useCallback(
    (list: ListModel, pagesize: number) => {
      list.paging.pagesize.value = pagesize;
      update(list, true);
    },
    [update],
  );

  return useMemo(
    () => ({
      update,
      updateFilters,
      updateFilter,
      updateSort,
      updatePage,
      updatePageSize,
      resetFilters,
      removeFilter,
    }),
    [
      removeFilter,
      resetFilters,
      update,
      updateFilter,
      updateFilters,
      updatePage,
      updatePageSize,
      updateSort,
    ],
  );
};
